import { useState } from 'react';
import { Button, Sheet } from '../ui';
import { useTranslation } from '../../i18n';

export interface GuestSignOutSheetProps {
  open: boolean;
  onClose: () => void;
  /** Opens "Save my account" instead of signing out. */
  onSaveAccount: () => void;
  /** Ends the guest profile on this device. */
  onSignOut: () => Promise<void>;
}

/**
 * Signing out of a guest profile cannot be undone (there is no password to
 * come back with), so it is warned about first and "Save my account" comes
 * before "Sign out anyway".
 */
export default function GuestSignOutSheet({
  open,
  onClose,
  onSaveAccount,
  onSignOut,
}: GuestSignOutSheetProps) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  const signOut = async () => {
    setBusy(true);
    try {
      await onSignOut();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title={t('guest.signOut.title')}>
      <div className="flex flex-col gap-4 pb-2">
        <p className="text-sm text-white/70">{t('guest.signOut.body')}</p>
        <div className="flex flex-col gap-2">
          <Button size="lg" fullWidth onClick={onSaveAccount} disabled={busy}>
            {t('guest.save.submit')}
          </Button>
          <Button
            variant="danger"
            fullWidth
            onClick={() => void signOut()}
            disabled={busy}
          >
            {busy ? t('guest.signOut.busy') : t('guest.signOut.confirm')}
          </Button>
          <Button variant="ghost" fullWidth onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    </Sheet>
  );
}
